import { useState, useEffect, useCallback, useMemo } from 'react';
import {
	AISuggestion,
	SuggestionFilters,
	CategoryStats,
	SuggestionCategory,
	SuggestionPriority,
	AISuggestionsResponse,
} from './AISuggestionsTypes';
import { aiSuggestionsService } from './AISuggestionsService';

interface UseAISuggestionsOptions {
	autoLoad?: boolean;
	refreshInterval?: number;
	filters?: SuggestionFilters;
}

interface UseAISuggestionsReturn {
	suggestions: AISuggestion[];
	isLoading: boolean;
	error: string | null;
	categoryStats: CategoryStats;
	totalCount: number;
	hasMore: boolean;
	refresh: () => Promise<void>;
	loadMore: () => Promise<void>;
	updateFilters: (filters: SuggestionFilters) => void;
	dismissSuggestion: (id: string) => Promise<void>;
	markAsRead: (id: string) => Promise<void>;
	snoozeSuggestion: (id: string, duration: number) => Promise<void>;
	getSuggestionsByCategory: (category: SuggestionCategory) => AISuggestion[];
	getSuggestionsByPriority: (priority: SuggestionPriority) => AISuggestion[];
	getUnreadCount: () => number;
	getHighPriorityCount: () => number;
}

export const useAISuggestions = (
	options: UseAISuggestionsOptions = {}
): UseAISuggestionsReturn => {
	const {
		autoLoad = true,
		refreshInterval,
		filters: initialFilters = {},
	} = options;

	// State
	const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [categoryStats, setCategoryStats] = useState<CategoryStats>({
		data: 0,
		projects: 0,
		knowledge: 0,
		calendar: 0,
		templates: 0,
	});
	const [totalCount, setTotalCount] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [filters, setFilters] = useState<SuggestionFilters>(initialFilters);

	// Load suggestions
	const loadSuggestions = useCallback(
		async (newFilters?: SuggestionFilters) => {
			setIsLoading(true);
			setError(null);

			try {
				const currentFilters = newFilters || filters;
				const response: AISuggestionsResponse =
					await aiSuggestionsService.getSuggestions(currentFilters);

				setSuggestions(response.suggestions);
				setTotalCount(response.total);
				setHasMore(response.hasMore);

				// Update category stats
				const stats = await aiSuggestionsService.getCategoryStats();
				setCategoryStats(stats);
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: 'Failed to load suggestions'
				);
				console.error('Error loading suggestions:', err);
			} finally {
				setIsLoading(false);
			}
		},
		[filters]
	);

	// Refresh suggestions
	const refresh = useCallback(async () => {
		await loadSuggestions();
	}, [loadSuggestions]);

	// Load more suggestions (for pagination)
	const loadMore = useCallback(async () => {
		if (!hasMore || isLoading) return;

		setIsLoading(true);
		try {
			// In a real implementation, you would pass pagination parameters
			const response: AISuggestionsResponse =
				await aiSuggestionsService.getSuggestions(filters);
			setSuggestions((prev) => [...prev, ...response.suggestions]);
			setHasMore(response.hasMore);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: 'Failed to load more suggestions'
			);
		} finally {
			setIsLoading(false);
		}
	}, [hasMore, isLoading, filters]);

	// Update filters
	const updateFilters = useCallback(
		(newFilters: SuggestionFilters) => {
			setFilters(newFilters);
			loadSuggestions(newFilters);
		},
		[loadSuggestions]
	);

	// Dismiss suggestion
	const dismissSuggestion = useCallback(async (id: string) => {
		try {
			await aiSuggestionsService.dismissSuggestion(id);
			setSuggestions((prev) =>
				prev.map((s) => (s.id === id ? { ...s, isDismissed: true } : s))
			);

			// Update category stats
			const stats = await aiSuggestionsService.getCategoryStats();
			setCategoryStats(stats);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: 'Failed to dismiss suggestion'
			);
		}
	}, []);

	// Mark suggestion as read
	const markAsRead = useCallback(async (id: string) => {
		try {
			await aiSuggestionsService.markAsRead(id);
			setSuggestions((prev) =>
				prev.map((s) => (s.id === id ? { ...s, isRead: true } : s))
			);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Failed to mark as read'
			);
		}
	}, []);

	// Snooze suggestion
	const snoozeSuggestion = useCallback(
		async (id: string, duration: number) => {
			try {
				await aiSuggestionsService.snoozeSuggestion(id, duration);
				setSuggestions((prev) =>
					prev.map((s) =>
						s.id === id
							? {
									...s,
									metadata: {
										...s.metadata,
										snoozeUntil: new Date(
											Date.now() + duration
										).toISOString(),
									},
								}
							: s
					)
				);
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: 'Failed to snooze suggestion'
				);
			}
		},
		[]
	);

	// Computed values
	const getSuggestionsByCategory = useCallback(
		(category: SuggestionCategory) => {
			return suggestions.filter(
				(s) => s.category === category && !s.isDismissed
			);
		},
		[suggestions]
	);

	const getSuggestionsByPriority = useCallback(
		(priority: SuggestionPriority) => {
			return suggestions.filter(
				(s) => s.priority === priority && !s.isDismissed
			);
		},
		[suggestions]
	);

	const getUnreadCount = useCallback(() => {
		return suggestions.filter((s) => !s.isRead && !s.isDismissed).length;
	}, [suggestions]);

	const getHighPriorityCount = useCallback(() => {
		return suggestions.filter(
			(s) => s.priority === 'high' && !s.isDismissed
		).length;
	}, [suggestions]);

	// Effects
	useEffect(() => {
		if (autoLoad) {
			loadSuggestions();
		}
	}, [autoLoad, loadSuggestions]);

	useEffect(() => {
		if (refreshInterval) {
			const interval = setInterval(() => {
				loadSuggestions();
			}, refreshInterval);

			return () => clearInterval(interval);
		}
	}, [refreshInterval, loadSuggestions]);

	return {
		suggestions,
		isLoading,
		error,
		categoryStats,
		totalCount,
		hasMore,
		refresh,
		loadMore,
		updateFilters,
		dismissSuggestion,
		markAsRead,
		snoozeSuggestion,
		getSuggestionsByCategory,
		getSuggestionsByPriority,
		getUnreadCount,
		getHighPriorityCount,
	};
};

// Hook for managing individual suggestion actions
export const useSuggestionActions = () => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const executeAction = useCallback(
		async (
			action: 'dismiss' | 'mark_read' | 'snooze' | 'customize',
			suggestionId: string,
			metadata?: any
		) => {
			setIsProcessing(true);
			setError(null);

			try {
				switch (action) {
					case 'dismiss':
						await aiSuggestionsService.dismissSuggestion(
							suggestionId
						);
						break;
					case 'mark_read':
						await aiSuggestionsService.markAsRead(suggestionId);
						break;
					case 'snooze':
						await aiSuggestionsService.snoozeSuggestion(
							suggestionId,
							metadata?.duration || 24 * 60 * 60 * 1000
						);
						break;
					case 'customize':
						await aiSuggestionsService.updateSuggestion({
							suggestionId,
							action: 'customize',
							metadata,
						});
						break;
				}
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: `Failed to ${action} suggestion`
				);
				throw err;
			} finally {
				setIsProcessing(false);
			}
		},
		[]
	);

	return {
		executeAction,
		isProcessing,
		error,
	};
};

// Hook for analytics
export const useAISuggestionsAnalytics = () => {
	const [analytics, setAnalytics] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadAnalytics = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const data = await aiSuggestionsService.getAnalytics();
			setAnalytics(data);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Failed to load analytics'
			);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadAnalytics();
	}, [loadAnalytics]);

	return {
		analytics,
		isLoading,
		error,
		refresh: loadAnalytics,
	};
};
